/**
 * Fire-and-forget wrapper around the `notify-event` Edge Function.
 *
 * Every mutation that should email teammates calls this after the
 * Supabase write returns OK. We never await it from the UI flow — if
 * the email send fails we want the local action to stay successful.
 *
 * Server-side, the dispatcher:
 *  - resolves the recipient list (subscribers / project member)
 *  - filters by each recipient's notification prefs
 *  - filters by includeOwnCards
 *  - always skips the actor (the JWT sub)
 *  - sends one email per recipient in their locale
 *
 * Adding a new event type is two steps:
 *  1. Extend NotifyEvent below with the new variant
 *  2. Handle it in supabase/functions/notify-event/index.ts
 */
import { supabase } from './supabase'

export type NotifyEvent =
  | {
      type: 'project_removed'
      project_id: string
      removed_user_id: string
    }
  | {
      type: 'card_status'
      ticket_id: string
      from_list_id: string
      to_list_id: string
    }
  | {
      // A new card was added (by a member / guest) on a project the
      // recipient owns. Notifies the project owner (excluding the actor).
      type: 'card_created'
      ticket_id: string
    }
  | {
      type: 'card_archived'
      ticket_id: string
    }
  | {
      // Mirror of card_archived — fired when a card is unarchived /
      // restored from the archive view. Same preference toggle gates
      // both (cardArchived covers archive + restore).
      type: 'card_restored'
      ticket_id: string
    }
  | {
      // The ticket row is gone by the time we call. Snapshot the
      // recipients + title client-side BEFORE delete so the dispatcher
      // can still email them.
      type: 'card_deleted'
      ticket_id: string
      project_id: string
      title: string
      subscriber_ids: string[]
    }
  | {
      type: 'card_priority'
      ticket_id: string
      from_priority: string | null
      to_priority: string | null
    }
  | {
      type: 'card_member'
      ticket_id: string
      // The new subscriber. They get a separate email via the existing
      // `notify-card-subscriber` function (different copy); the dispatcher
      // notifies the OTHER existing subscribers.
      joiner_user_id: string
    }
  | {
      // Internal team comment posted on the card. Notifies the
      // subscribers (minus the author themselves).
      type: 'card_comment'
      ticket_id: string
      // Truncated/full body of the comment for the email excerpt.
      // The dispatcher truncates again for safety.
      comment_body: string
    }
  | {
      // Two cards were merged. The survivor (ticket_id) absorbed the
      // absorbed card, which is then deleted. We snapshot the union of
      // both cards' subscribers client-side so the dispatcher can still
      // email everyone (the absorbed card's rows cascade away on delete).
      type: 'card_merged'
      ticket_id: string
      project_id: string
      survivor_title: string
      absorbed_title: string
      subscriber_ids: string[]
    }

/** Fire the dispatcher. Returns a promise but the caller usually
 *  doesn't await — `void notifyEvent(...)` is the idiomatic pattern. */
export async function notifyEvent(event: NotifyEvent): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('notify-event', {
      body: event,
    })
    if (error) {
      // Stay quiet — a failed notification email shouldn't disrupt the
      // UI, but we log so production issues surface in DevTools.
      console.warn('notify-event failed', event.type, error.message)
    }
  } catch (e) {
    console.warn('notify-event threw', event.type, e)
  }
}
